import type { CardContext, CardDef } from '../core/combat';
import {
  addPower,
  currentTarget,
  damageEnemy,
  placePieceAt,
} from '../core/combat';
import {
  WELL_COLS,
  WELL_ROWS,
  idx,
  idxCol,
  idxRow,
  type GemKind,
  type Grid,
  type Piece,
} from '../core/types';
import { applyGravity, countGems, countKind } from '../core/grid';
import { occupiedCells } from '../core/intent';
import { makePiece, type ShapeDef, type ColorRecipe } from './shapes';

// --- pieces ---------------------------------------------------------------

const EMBER_SHARD_PIECE = { cells: [[0, 0]] as const, kinds: ['ember'] as const };
const FROST_SHARD_PIECE = { cells: [[0, 0]] as const, kinds: ['frost'] as const };
const DROP_PIECE = {
  cells: [[0, 0], [1, 0], [2, 0], [3, 0]] as const,
  kinds: ['ember', 'frost', 'ember', 'frost'] as const,
};
const HARD_DROP_PIECE = {
  cells: [[0, 0], [0, 1], [1, 0], [1, 1]] as const,
  kinds: ['ember', 'frost', 'ember', 'frost'] as const,
};
const T_SPIN_PIECE = {
  cells: [[0, 0], [0, 1], [0, 2], [1, 1]] as const,
  // Alternating colours keep the piece from matching itself. Its payoff must
  // come from fitting one of its arms into a run already prepared in the well.
  kinds: ['venom', 'volt', 'venom', 'volt'] as const,
};
const FROST_WALL_PIECE = {
  cells: [[0, 0], [0, 1], [0, 2], [0, 3]] as const,
  kinds: ['frost', 'frost', 'frost', 'frost'] as const,
};
const PILLAR_PIECE = {
  cells: [[0, 0], [1, 0], [2, 0], [3, 0]] as const,
  kinds: ['ember', 'ember', 'ember', 'ember'] as const,
};
// The three-cell tetrominoes. Colours are mixed on purpose: rotating the piece
// re-orders them, so where you drop it and how you turn it is the decision.
const S_PIECE = {
  cells: [[0, 1], [0, 2], [1, 0], [1, 1]] as const,
  kinds: ['ember', 'frost', 'frost', 'ember'] as const,
};
const Z_PIECE = {
  cells: [[0, 0], [0, 1], [1, 1], [1, 2]] as const,
  kinds: ['frost', 'ember', 'ember', 'frost'] as const,
};
const J_PIECE = {
  cells: [[0, 0], [1, 0], [2, 0], [2, 1]] as const,
  kinds: ['ember', 'ember', 'venom', 'venom'] as const,
};
const L_PIECE = {
  cells: [[0, 1], [1, 1], [2, 0], [2, 1]] as const,
  kinds: ['frost', 'frost', 'ember', 'ember'] as const,
};

// --- shape / recipe factory ------------------------------------------------
// Geometry and color recipe are authored separately so the same silhouette can
// carry different strategic purposes. New variant cards use makePiece(); the
// legacy pieces above are untouched.

const SHAPE_T: ShapeDef = { id: 'shape_t', cells: [[0, 0], [0, 1], [0, 2], [1, 1]] };
const SHAPE_S: ShapeDef = { id: 'shape_s', cells: [[0, 1], [0, 2], [1, 0], [1, 1]] };
const SHAPE_Z: ShapeDef = { id: 'shape_z', cells: [[0, 0], [0, 1], [1, 1], [1, 2]] };
const SHAPE_J: ShapeDef = { id: 'shape_j', cells: [[0, 0], [1, 0], [2, 0], [2, 1]] };
const SHAPE_I4: ShapeDef = { id: 'shape_i4', cells: [[0, 0], [1, 0], [2, 0], [3, 0]] };
const SHAPE_I3: ShapeDef = { id: 'shape_i3', cells: [[0, 0], [1, 0], [2, 0]] };
const SHAPE_L3: ShapeDef = { id: 'shape_l3', cells: [[0, 0], [1, 0], [1, 1]] };
const SHAPE_V3: ShapeDef = { id: 'shape_v3', cells: [[0, 0], [0, 1], [1, 0]] };
const SHAPE_P: ShapeDef = { id: 'shape_p', cells: [[0, 0], [0, 1], [1, 0], [1, 1], [2, 0]] };

const RECIPE_CONDUCTOR: ColorRecipe = { id: 'recipe_conductor', kinds: ['volt', 'frost', 'volt', 'frost'] };
const RECIPE_FURNACE: ColorRecipe = { id: 'recipe_furnace', kinds: ['ember', 'ember', 'volt', 'ember'] };
const RECIPE_TOXIC: ColorRecipe = { id: 'recipe_toxic', kinds: ['venom', 'venom', 'frost', 'venom'] };
const RECIPE_PRISM: ColorRecipe = { id: 'recipe_prism', kinds: ['ember', 'frost', 'venom', 'volt'] };
const RECIPE_EMBER_S: ColorRecipe = { id: 'recipe_ember_s', kinds: ['ember', 'ember', 'ember', 'frost'] };
const RECIPE_VOLT_Z: ColorRecipe = { id: 'recipe_volt_z', kinds: ['volt', 'volt', 'ember', 'volt'] };
const RECIPE_FROST_HOOK: ColorRecipe = { id: 'recipe_frost_hook', kinds: ['frost', 'frost', 'ember', 'frost'] };
const RECIPE_NEEDLE: ColorRecipe = { id: 'recipe_needle', kinds: ['venom', 'volt', 'frost'] };
const RECIPE_PIVOT: ColorRecipe = { id: 'recipe_pivot', kinds: ['ember', 'venom', 'volt'] };
const RECIPE_QUICK: ColorRecipe = { id: 'recipe_quick', kinds: ['frost', 'ember', 'venom'] };
const RECIPE_PBLOCK: ColorRecipe = { id: 'recipe_pblock', kinds: ['ember', 'ember', 'frost', 'frost', 'venom'] };
const RECIPE_MONOLITH: ColorRecipe = { id: 'recipe_monolith', kinds: ['venom', 'venom', 'venom', 'venom'] };
const RECIPE_STONE_TENSION: ColorRecipe = { id: 'recipe_stone_tension', kinds: ['ember', 'ember', 'stone', 'ember'] };

const CONDUCTOR_T_PIECE = makePiece(SHAPE_T, RECIPE_CONDUCTOR);
const FURNACE_T_PIECE = makePiece(SHAPE_T, RECIPE_FURNACE);
const TOXIC_T_PIECE = makePiece(SHAPE_T, RECIPE_TOXIC);
const PRISM_T_PIECE = makePiece(SHAPE_T, RECIPE_PRISM);
const EMBER_S_PIECE = makePiece(SHAPE_S, RECIPE_EMBER_S);
const VOLT_Z_PIECE = makePiece(SHAPE_Z, RECIPE_VOLT_Z);
const FROST_HOOK_PIECE = makePiece(SHAPE_J, RECIPE_FROST_HOOK);
const NEEDLE_PIECE = makePiece(SHAPE_I3, RECIPE_NEEDLE);
const PIVOT_PIECE = makePiece(SHAPE_L3, RECIPE_PIVOT);
const QUICK_SETUP_PIECE = makePiece(SHAPE_V3, RECIPE_QUICK);
const P_BLOCK_PIECE = makePiece(SHAPE_P, RECIPE_PBLOCK);
const MONOLITH_PIECE = makePiece(SHAPE_I4, RECIPE_MONOLITH);
const STONE_TENSION_PIECE = makePiece(SHAPE_T, RECIPE_STONE_TENSION);

function drop(piece: Piece) {
  return (ctx: CardContext) => {
    if (ctx.column === undefined) return;
    placePieceAt(ctx.combat, piece, ctx.column, ctx.rotation ?? 0);
  };
}

/** Stone worth removing, lowest rows first — those are the ones about to cap. */
function stonesLowestFirst(grid: Grid): number[] {
  return occupiedCells(grid).filter(i => grid[i] === 'stone').sort((a, b) => b - a);
}

function damageAllEnemies(ctx: CardContext, amount: number): void {
  for (const enemy of ctx.combat.enemies) {
    if (enemy.hp > 0) damageEnemy(ctx.combat, enemy, amount);
  }
}

// --- starter deck ---------------------------------------------------------

export const EMBER_SHARD: CardDef = {
  id: 'ember_shard',
  name: 'Ember Shard',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  text: 'Place an Ember gem (damage) in a column.',
  piece: EMBER_SHARD_PIECE,
  effect: drop(EMBER_SHARD_PIECE),
};

export const FROST_SHARD: CardDef = {
  id: 'frost_shard',
  name: 'Frost Shard',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  text: 'Place a Frost gem (block) in a column.',
  piece: FROST_SHARD_PIECE,
  effect: drop(FROST_SHARD_PIECE),
};

export const NUDGE: CardDef = {
  id: 'nudge',
  name: 'Nudge',
  cost: 0,
  target: 'swap',
  rarity: 'common',
  text: 'Swap two adjacent gems.',
  effect: (ctx) => {
    const a = ctx.cell;
    const b = ctx.swapWith;
    if (a === undefined || b === undefined) return;
    const dr = Math.abs(idxRow(a) - idxRow(b));
    const dc = Math.abs(idxCol(a) - idxCol(b));
    if (dr + dc !== 1) return;
    const ka = ctx.grid[a] ?? null;
    const kb = ctx.grid[b] ?? null;
    if (ka === null || kb === null) return;
    ctx.grid[a] = kb;
    ctx.grid[b] = ka;
  },
};

export const DROP_PIECE_CARD: CardDef = {
  id: 'drop_piece',
  name: 'Drop Piece',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  text: 'Place a 1x4 I-piece: Ember/Frost.',
  piece: DROP_PIECE,
  effect: drop(DROP_PIECE),
};

export const HARD_DROP: CardDef = {
  id: 'hard_drop',
  name: 'Hard Drop',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  text: 'Place a 2x2 O-piece: 2 Ember, 2 Frost.',
  piece: HARD_DROP_PIECE,
  effect: drop(HARD_DROP_PIECE),
};

// --- tetromino drops ------------------------------------------------------

export const SKEW_DROP: CardDef = {
  id: 'skew_drop',
  name: 'Skew Drop',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  text: 'Place an S-piece (Ember/Frost). Match: +2 Block.',
  piece: S_PIECE,
  effect: drop(S_PIECE),
  afterResolve: (ctx, report) => {
    if (report.waves.some(w => w.runs.length > 0)) ctx.combat.player.block += 2;
  },
};

export const ZIG_DROP: CardDef = {
  id: 'zig_drop',
  name: 'Zig Drop',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  text: 'Place a Z-piece (Frost/Ember). Match: +1 Energy.',
  piece: Z_PIECE,
  effect: drop(Z_PIECE),
  afterResolve: (ctx, report) => {
    if (report.waves.some(w => w.runs.length > 0)) ctx.combat.player.energy += 1;
  },
};

export const HOOK_DROP: CardDef = {
  id: 'hook_drop',
  name: 'Hook Drop',
  cost: 1,
  target: 'piece',
  rarity: 'uncommon',
  text: 'Place a J-piece (Ember/Venom). Row: 8 damage.',
  piece: J_PIECE,
  effect: drop(J_PIECE),
  afterResolve: (ctx, report) => {
    if (report.waves.some(w => w.rowsCleared.length > 0)) {
      const target = currentTarget(ctx.combat);
      if (target) damageEnemy(ctx.combat, target, 8);
    }
  },
};

export const CORNER_DROP: CardDef = {
  id: 'corner_drop',
  name: 'Corner Drop',
  cost: 2,
  target: 'piece',
  rarity: 'uncommon',
  text: 'Place an L-piece (Frost/Ember). Match: +4 Block.',
  piece: L_PIECE,
  effect: drop(L_PIECE),
  afterResolve: (ctx, report) => {
    if (report.waves.some(w => w.runs.length > 0)) ctx.combat.player.block += 4;
  },
};

// --- common pool ----------------------------------------------------------

export const T_SPIN: CardDef = {
  id: 't_spin',
  name: 'T-Spin',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  text: 'Place a T-piece: 2 Venom + 2 Volt. Match: +1 Energy.',
  piece: T_SPIN_PIECE,
  effect: drop(T_SPIN_PIECE),
  afterResolve: (ctx, report) => {
    if (report.waves.some(w => w.runs.length > 0)) ctx.combat.player.energy += 1;
  },
};

export const JUNK_SHOT: CardDef = {
  id: 'junk_shot',
  name: 'Junk Shot',
  cost: 1,
  target: 'none',
  rarity: 'common',
  text: 'Destroy 3 random Stone gems.',
  effect: (ctx) => {
    const stones = occupiedCells(ctx.grid).filter(i => ctx.grid[i] === 'stone');
    const toDestroy = Math.min(3, stones.length);
    for (let n = 0; n < toDestroy; n++) {
      const pick = Math.floor(ctx.rng() * stones.length);
      const cell = stones.splice(pick, 1)[0];
      if (cell !== undefined) ctx.grid[cell] = null;
    }
  },
};

export const FROST_WALL: CardDef = {
  id: 'frost_wall',
  name: 'Frost Wall',
  cost: 2,
  target: 'piece',
  rarity: 'common',
  text: 'Place a 1x4 Frost I-piece (block).',
  piece: FROST_WALL_PIECE,
  effect: drop(FROST_WALL_PIECE),
};

export const VOLT_CONDUIT: CardDef = {
  id: 'volt_conduit',
  name: 'Volt Conduit',
  cost: 1,
  target: 'column',
  rarity: 'common',
  text: 'Convert a column to Volt (energy).',
  effect: (ctx) => {
    if (ctx.column === undefined) return;
    for (let r = 0; r < WELL_ROWS; r++) {
      const i = idx(r, ctx.column);
      if (ctx.grid[i] !== null) ctx.grid[i] = 'volt';
    }
  },
};

export const SWEEPER: CardDef = {
  id: 'sweeper',
  name: 'Sweeper',
  cost: 1,
  target: 'none',
  rarity: 'common',
  text: 'Destroy the bottom row.',
  effect: (ctx) => {
    for (let c = 0; c < WELL_COLS; c++) ctx.grid[idx(WELL_ROWS - 1, c)] = null;
  },
};

export const GRAVITY_WELL: CardDef = {
  id: 'gravity_well',
  name: 'Gravity Well',
  cost: 1,
  target: 'none',
  rarity: 'common',
  text: 'All gems compact downward. Resolve matches.',
  effect: (ctx) => {
    applyGravity(ctx.grid);
  },
};

export const PRISM: CardDef = {
  id: 'prism',
  name: 'Prism',
  cost: 2,
  target: 'color',
  rarity: 'common',
  text: 'Choose a color; those gems become Ember (damage).',
  effect: (ctx) => {
    if (ctx.color === undefined) return;
    for (let i = 0; i < ctx.grid.length; i++) {
      if (ctx.grid[i] === ctx.color) ctx.grid[i] = 'ember';
    }
  },
};

export const CASCADE_CALL: CardDef = {
  id: 'cascade_call',
  name: 'Cascade Call',
  cost: 2,
  target: 'cell',
  rarity: 'common',
  text: 'Destroy a 3x3 area.',
  effect: (ctx) => {
    if (ctx.cell === undefined) return;
    const cr = idxRow(ctx.cell);
    const cc = idxCol(ctx.cell);
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        const r = cr + dr;
        const c = cc + dc;
        if (r >= 0 && r < WELL_ROWS && c >= 0 && c < WELL_COLS) ctx.grid[idx(r, c)] = null;
      }
    }
  },
};

// --- uncommon -------------------------------------------------------------

export const CHAIN_REACTION: CardDef = {
  id: 'chain_reaction',
  name: 'Chain Reaction',
  cost: 2,
  target: 'none',
  rarity: 'uncommon',
  power: true,
  text: 'Power. Cascades of 3+ grant 2 Energy.',
  effect: (ctx) => {
    addPower(ctx.combat, 'chain_reaction', 'Chain Reaction');
  },
};

export const GEM_SOCKET: CardDef = {
  id: 'gem_socket',
  name: 'Gem Socket',
  cost: 1,
  target: 'none',
  rarity: 'uncommon',
  power: true,
  text: 'Power. Whenever you clear a row, gain 4 Block.',
  effect: (ctx) => {
    addPower(ctx.combat, 'gem_socket', 'Gem Socket');
  },
};

export const OVERLOAD: CardDef = {
  id: 'overload',
  name: 'Overload',
  cost: 2,
  target: 'none',
  rarity: 'uncommon',
  text: 'Deal 3 damage per Volt (energy) gem in the Well.',
  effect: (ctx) => {
    const volts = countKind(ctx.grid, 'volt');
    if (volts <= 0) return;
    const target = currentTarget(ctx.combat);
    if (target) damageEnemy(ctx.combat, target, volts * 3);
  },
};

export const REFINE: CardDef = {
  id: 'refine',
  name: 'Refine',
  cost: 1,
  target: 'none',
  rarity: 'uncommon',
  text: 'Turn up to 5 Stone into Ember (damage).',
  effect: (ctx) => {
    for (const i of stonesLowestFirst(ctx.grid).slice(0, 5)) ctx.grid[i] = 'ember';
  },
};

// --- rare -----------------------------------------------------------------

export const PILLAR: CardDef = {
  id: 'pillar',
  name: 'Pillar',
  cost: 3,
  target: 'piece',
  rarity: 'rare',
  text: 'Place a 4x1 Ember I-piece. Clear 4 rows: 50 to ALL enemies.',
  piece: PILLAR_PIECE,
  effect: drop(PILLAR_PIECE),
  afterResolve: (ctx, report) => {
    // A single wave that cleared four rows — one gesture, not a sum across waves.
    if (report.waves.some(w => w.rowsCleared.length >= 4)) {
      damageAllEnemies(ctx, 50);
    }
  },
};

export const BLACK_HOLE: CardDef = {
  id: 'black_hole',
  name: 'Black Hole',
  cost: 3,
  target: 'none',
  rarity: 'rare',
  text: 'Destroy the center column and both neighbors.',
  effect: (ctx) => {
    for (const c of [2, 3, 4]) {
      for (let r = 0; r < WELL_ROWS; r++) ctx.grid[idx(r, c)] = null;
    }
  },
};

export const PERFECT_CLEAR: CardDef = {
  id: 'perfect_clear',
  name: 'Perfect Clear',
  cost: 2,
  target: 'none',
  rarity: 'rare',
  text: 'Destroy all gems. If 20+ destroyed, deal 30 to ALL enemies.',
  effect: (ctx) => {
    const destroyed = countGems(ctx.grid);
    ctx.grid.fill(null);
    return destroyed;
  },
  afterResolve: (ctx, _report, value) => {
    if ((value ?? 0) >= 20) damageAllEnemies(ctx, 30);
  },
};

export const METRONOME: CardDef = {
  id: 'metronome',
  name: 'Metronome',
  cost: 2,
  target: 'none',
  rarity: 'rare',
  power: true,
  text: 'Power. Start of each turn: place a random gem in a random column.',
  effect: (ctx) => {
    addPower(ctx.combat, 'metronome', 'Metronome');
  },
};

// --- color variants (same geometry, different recipes) ---------------------

export const CONDUCTOR_T: CardDef = {
  id: 't_conductor',
  name: 'Conductor T',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  geometryId: 'shape_t',
  text: 'Place a T-piece: 2 Volt + 2 Frost. Match: +1 Energy.',
  piece: CONDUCTOR_T_PIECE,
  effect: drop(CONDUCTOR_T_PIECE),
  afterResolve: (ctx, report) => {
    if (report.waves.some(w => w.runs.length > 0)) ctx.combat.player.energy += 1;
  },
};

export const FURNACE_T: CardDef = {
  id: 't_furnace',
  name: 'Furnace T',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  geometryId: 'shape_t',
  text: 'Place a T-piece: 3 Ember + 1 Volt. Match: +3 damage.',
  piece: FURNACE_T_PIECE,
  effect: drop(FURNACE_T_PIECE),
  afterResolve: (ctx, report) => {
    if (report.waves.some(w => w.runs.length > 0)) {
      const target = currentTarget(ctx.combat);
      if (target) damageEnemy(ctx.combat, target, 3);
    }
  },
};

export const TOXIC_T: CardDef = {
  id: 't_toxic',
  name: 'Toxic T',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  geometryId: 'shape_t',
  text: 'Place a T-piece: 3 Venom + 1 Frost. Poison setup.',
  piece: TOXIC_T_PIECE,
  effect: drop(TOXIC_T_PIECE),
};

export const PRISM_T: CardDef = {
  id: 't_prism',
  name: 'Prism T',
  cost: 2,
  target: 'piece',
  rarity: 'uncommon',
  geometryId: 'shape_t',
  text: 'Place a T-piece: one of each color. Flexibility.',
  piece: PRISM_T_PIECE,
  effect: drop(PRISM_T_PIECE),
};

export const EMBER_S: CardDef = {
  id: 's_ember',
  name: 'Ember S',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  geometryId: 'shape_s',
  text: 'Place an S-piece: 3 Ember + 1 Frost. Match: +2 Block.',
  piece: EMBER_S_PIECE,
  effect: drop(EMBER_S_PIECE),
  afterResolve: (ctx, report) => {
    if (report.waves.some(w => w.runs.length > 0)) ctx.combat.player.block += 2;
  },
};

export const VOLT_Z: CardDef = {
  id: 'z_volt',
  name: 'Volt Z',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  geometryId: 'shape_z',
  text: 'Place a Z-piece: 3 Volt + 1 Ember. Match: +1 Energy.',
  piece: VOLT_Z_PIECE,
  effect: drop(VOLT_Z_PIECE),
  afterResolve: (ctx, report) => {
    if (report.waves.some(w => w.runs.length > 0)) ctx.combat.player.energy += 1;
  },
};

export const FROST_HOOK: CardDef = {
  id: 'j_frost',
  name: 'Frost Hook',
  cost: 1,
  target: 'piece',
  rarity: 'uncommon',
  geometryId: 'shape_j',
  text: 'Place a J-piece: 3 Frost + 1 Ember. Match: +3 Block.',
  piece: FROST_HOOK_PIECE,
  effect: drop(FROST_HOOK_PIECE),
  afterResolve: (ctx, report) => {
    if (report.waves.some(w => w.runs.length > 0)) ctx.combat.player.block += 3;
  },
};

// --- new geometry ----------------------------------------------------------

export const NEEDLE: CardDef = {
  id: 'i3_needle',
  name: 'Needle',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  geometryId: 'shape_i3',
  text: 'Place a vertical I3: Venom/Volt/Frost. Easy to fit.',
  piece: NEEDLE_PIECE,
  effect: drop(NEEDLE_PIECE),
};

export const PIVOT: CardDef = {
  id: 'l3_pivot',
  name: 'Pivot',
  cost: 1,
  target: 'piece',
  rarity: 'common',
  geometryId: 'shape_l3',
  text: 'Place an L3: Ember/Venom/Volt. Compact setup piece.',
  piece: PIVOT_PIECE,
  effect: drop(PIVOT_PIECE),
};

export const P_BLOCK: CardDef = {
  id: 'p_block',
  name: 'P-block',
  cost: 2,
  target: 'piece',
  rarity: 'uncommon',
  geometryId: 'shape_p',
  text: 'Place a P-pentomino: 2 Ember, 2 Frost, 1 Venom. High commitment.',
  piece: P_BLOCK_PIECE,
  effect: drop(P_BLOCK_PIECE),
};

export const MONOLITH: CardDef = {
  id: 'i4_monolith',
  name: 'Monolith',
  cost: 2,
  target: 'piece',
  rarity: 'uncommon',
  geometryId: 'shape_i4',
  text: 'Place a 4x1 Venom I-piece. Charged 4-match: heavy Poison.',
  piece: MONOLITH_PIECE,
  effect: drop(MONOLITH_PIECE),
};

// --- weird cards -----------------------------------------------------------

export const STONE_TENSION: CardDef = {
  id: 'stone_tension',
  name: 'Stone Tension',
  cost: 1,
  target: 'piece',
  rarity: 'uncommon',
  geometryId: 'shape_t',
  text: 'Place a T-piece: 3 Ember + 1 Stone. Great setup, leaves pollution.',
  piece: STONE_TENSION_PIECE,
  effect: drop(STONE_TENSION_PIECE),
};

export const QUICK_SETUP: CardDef = {
  id: 'quick_setup',
  name: 'Quick Setup',
  cost: 0,
  target: 'piece',
  rarity: 'common',
  geometryId: 'shape_v3',
  text: 'Place a V3: Frost/Ember/Venom. Free — enables setup.',
  piece: QUICK_SETUP_PIECE,
  effect: drop(QUICK_SETUP_PIECE),
};

// --- board manipulation ----------------------------------------------------

const COLOR_OPPOSITE: Record<GemKind, GemKind> = {
  ember: 'frost',
  frost: 'ember',
  venom: 'volt',
  volt: 'venom',
  stone: 'ember',
};

export const GEM_SWAP: CardDef = {
  id: 'gem_swap',
  name: 'Gem Swap',
  cost: 1,
  target: 'swap',
  rarity: 'common',
  text: 'Swap two adjacent gems, including Stone.',
  effect: (ctx) => {
    const a = ctx.cell;
    const b = ctx.swapWith;
    if (a === undefined || b === undefined) return;
    const dr = Math.abs(idxRow(a) - idxRow(b));
    const dc = Math.abs(idxCol(a) - idxCol(b));
    if (dr + dc !== 1) return;
    const ka = ctx.grid[a] ?? null;
    const kb = ctx.grid[b] ?? null;
    if (ka === null || kb === null) return;
    ctx.grid[a] = kb;
    ctx.grid[b] = ka;
  },
};

export const COLOR_SHIFT: CardDef = {
  id: 'color_shift',
  name: 'Color Shift',
  cost: 1,
  target: 'color',
  rarity: 'uncommon',
  text: 'Choose a color; gems flip to the opposite color.',
  effect: (ctx) => {
    if (ctx.color === undefined) return;
    const to = COLOR_OPPOSITE[ctx.color];
    for (let i = 0; i < ctx.grid.length; i++) {
      if (ctx.grid[i] === ctx.color) ctx.grid[i] = to;
    }
  },
};

// --- decks ----------------------------------------------------------------

/**
 * The 10-card starting deck.
 *
 * Gemfall's verb is dropping shapes, so the opening hand must advertise that
 * verb immediately. The deck now includes three T-shape variants with
 * different color recipes, plus the classic I/O/S/Z silhouettes, so all four
 * gem colors appear naturally without making the deck powerful.
 */
export const STARTER_DECK: readonly CardDef[] = [
  DROP_PIECE_CARD, DROP_PIECE_CARD,
  HARD_DROP,
  SKEW_DROP,
  ZIG_DROP, ZIG_DROP,
  T_SPIN,
  CONDUCTOR_T,
  PRISM_T,
  FURNACE_T,
];

/** Cards offered as post-battle rewards. Powers included. */
export const REWARD_POOL: readonly CardDef[] = [
  T_SPIN, JUNK_SHOT, FROST_WALL, VOLT_CONDUIT, SWEEPER, GRAVITY_WELL, PRISM, CASCADE_CALL,
  SKEW_DROP, ZIG_DROP, HOOK_DROP, CORNER_DROP,
  CHAIN_REACTION, GEM_SOCKET, OVERLOAD, REFINE,
  PILLAR, BLACK_HOLE, PERFECT_CLEAR, METRONOME,
  CONDUCTOR_T, FURNACE_T, TOXIC_T, PRISM_T,
  EMBER_S, VOLT_Z, FROST_HOOK,
  NEEDLE, PIVOT, P_BLOCK, MONOLITH,
  STONE_TENSION, QUICK_SETUP,
  GEM_SWAP, COLOR_SHIFT,
];

/** Every distinct card in the game. */
export const ALL_CARDS: readonly CardDef[] = [
  EMBER_SHARD, FROST_SHARD, NUDGE, DROP_PIECE_CARD, HARD_DROP,
  ...REWARD_POOL,
];

/**
 * Gem color counts across all piece cards in a deck. Useful for debugging
 * color diversity — call it on STARTER_DECK or any run deck to see the
 * distribution at a glance.
 */
export function deckColorProfile(deck: readonly CardDef[]): Record<GemKind, number> {
  const profile: Record<GemKind, number> = {
    ember: 0, frost: 0, venom: 0, volt: 0, stone: 0,
  };
  for (const card of deck) {
    if (!card.piece) continue;
    for (const kind of card.piece.kinds) profile[kind]++;
  }
  return profile;
}
